import React, { useState } from 'react';
import './Calibration.scss';
import { Button } from '@material-ui/core';
import { ScaleLoader } from 'react-spinners';
import { useBem } from '../Bem';
import {
  steps,
  useAccessChecker,
  useAdasImages,
  useCurrentMeasurment,
  useDeviceMetadata,
  useDeviceTelemetry,
  useImageCoeficient,
  useImageUrl,
  useRequestAdasImages,
  useToken,
  useUserInfo,
  closeAppDialog,
  successfullyCalibrated,
  calibrationError,
} from './helper';
import { useIntl } from 'react-intl';
import { CalibrationIntl } from './Calibration.intl';
import { Form, Formik } from 'formik';
import { useMutation } from 'react-query';
import { InfoScreen } from './InfoScreen';
import { calibrateDevice } from './service';
import { useParams } from 'react-router';
import { UrlParams, Values } from './models';
import { MeasurementList } from './MeasurementList/MeasurementList';

export const CalibrationForm = () => { // NOSONAR
  const bem = useBem('calibration');
  const intl = useIntl();
  const [currentStep, setCurrentStep] = useState(1);
  const { deviceImei } = useParams<UrlParams>();
  const currentMeasurment = useCurrentMeasurment();

  const { isUserLoading, hasUserError } = useUserInfo();
  const { isError, isLoading } = useDeviceMetadata(deviceImei);
  const { response, setResponse } = useAccessChecker(deviceImei);
  const token = useToken();
  const mutationCalibrateDevice = useMutation((values: Values) => calibrateDevice(deviceImei, values, token));
  const img = useRequestAdasImages(deviceImei);
  const { deviceTelemetry, isTelemetryLoading, isTelemetryError } = useDeviceTelemetry(deviceImei);
  const { isLoadingAdas } = useAdasImages(deviceImei);

  const selectedImage = useImageUrl();
  const { heightCoef, widthCoef } = useImageCoeficient();

  if (
    isLoadingAdas ||
    isLoading ||
    isUserLoading ||
    img.isLoadingAdas ||
    isTelemetryLoading ||
    !currentMeasurment?.coef
  ) {
    return (
      <div className={bem.elem('app-loader').cls()}>
          <ScaleLoader color='#007AFF' width={4} radius={2} height={35} />
      </div>
    );
  }

  if (isError || hasUserError || isTelemetryError) {
    return (
      <InfoScreen
        type={intl.formatMessage(CalibrationIntl.error)}
        message={intl.formatMessage(CalibrationIntl.errorGeneralMessage)}
        action={{
          title: intl.formatMessage(CalibrationIntl.close),
          method: () => closeAppDialog(),
        }}
      />
    );
  }

  if (response) {
    return <InfoScreen {...response} />;
  }
  const checkStep2 = (values: Values) => {
    const errors = {} as any;
    const from = Math.ceil(100 / currentMeasurment.coef);
    const to = Math.floor(400 / currentMeasurment.coef);
    if (!values?.cameraHeight) {
      errors['cameraHeight'] = intl.formatMessage(CalibrationIntl.cameraHeightRequired);
    }
    if (values.cameraHeight) {
      if (values.cameraHeight < from || values.cameraHeight > to) {
        errors['cameraHeight'] = intl.formatMessage(CalibrationIntl.cameraHeightNotCorrectValue, {
          restriction: `from ${from} to ${to} ${currentMeasurment.value.toLowerCase()}`,
        });
      }
      if (!Number.isInteger(+values.cameraHeight)) {
        errors['cameraHeight'] = intl.formatMessage(CalibrationIntl.isNotInteger);
      }
    }
    return errors;
  };
  const checkStep3 = (values: Values) => {
    const errors = {} as any;
    const from = Math.ceil(100 / currentMeasurment.coef);
    const to = Math.floor(400 / currentMeasurment.coef);
    if (!values?.vehicleWidth) {
      errors['vehicleWidth'] = intl.formatMessage(CalibrationIntl.vehicleWidthRequired);
    }
    if (values.vehicleWidth) {
      if (values.vehicleWidth < from || values.vehicleWidth > to) {
        errors['vehicleWidth'] = intl.formatMessage(CalibrationIntl.vehicleWidthCorrectValue, {
          restriction: `from ${from} to ${to} ${currentMeasurment.value.toLowerCase()}`,
        });
      }
      if (!Number.isInteger(+values.vehicleWidth)) {
        errors['vehicleWidth'] = intl.formatMessage(CalibrationIntl.isNotInteger);
      }
    }
    return errors;
  };
  const checkStep4 = (values: Values) => {
    const errors = {} as any;
    const from = Math.ceil(-200 / currentMeasurment.coef);
    const to = Math.floor(200 / currentMeasurment.coef);
    if (!values?.cameraOffset) {
      errors['cameraOffset'] = intl.formatMessage(CalibrationIntl.cameraOffsetRequired);
    }
    if (values.cameraOffset) {
      if (values.cameraOffset < from || values.cameraOffset > to) {
        errors['cameraOffset'] = intl.formatMessage(CalibrationIntl.cameraOffsetNotCorrectValue, {
          restriction: `from ${from} to ${to} ${currentMeasurment.value.toLowerCase()}`,
        });
      }
      if (!Number.isInteger(+values.cameraOffset)) {
        errors['cameraOffset'] = intl.formatMessage(CalibrationIntl.isNotInteger);
      }
    }
    return errors;
  };
  const validate: any = {
    2: (values: Values) => ({ ...checkStep2(values) }),
    3: (values: Values) => ({ ...checkStep3(values) }),
    4: (values: Values) => ({ ...checkStep4(values) }),
    7: (values: Values) => ({ ...checkStep2(values), ...checkStep3(values), ...checkStep4(values) }),
  };

  const prepareValue = ({
    lowestUsableRow,
    calpickTopLeftCol,
    calpickTopLeftRow,
    calpickBottomLeftCol,
    calpickBottomLeftRow,
    calpickTopRightCol,
    calpickTopRightRow,
    calpickBottomRightCol,
    calpickBottomRightRow,
    cameraHeight,
    cameraOffset,
    vehicleWidth,
  }: Values) => ({
    cameraHeight: Math.round(cameraHeight * currentMeasurment.coef),
    cameraOffset: Math.round(cameraOffset * currentMeasurment.coef),
    vehicleWidth: Math.round(vehicleWidth * currentMeasurment.coef),
    lowestUsableRow: Math.round(lowestUsableRow / heightCoef),
    calpickTopLeftCol: Math.round(calpickTopLeftCol / widthCoef),
    calpickTopLeftRow: Math.round(calpickTopLeftRow / heightCoef),

    calpickBottomLeftCol: Math.round(calpickBottomLeftCol / widthCoef),
    calpickBottomLeftRow: Math.round(calpickBottomLeftRow / heightCoef),

    calpickTopRightCol: Math.round(calpickTopRightCol / widthCoef),
    calpickTopRightRow: Math.round(calpickTopRightRow / heightCoef),

    calpickBottomRightCol: Math.round(calpickBottomRightCol / widthCoef),
    calpickBottomRightRow: Math.round(calpickBottomRightRow / heightCoef),
  });

  const handleSubmit = async (values: Values) => {
    if (currentStep < steps.length) {
      setCurrentStep((currentStep) => ++currentStep);
    } else {
      mutationCalibrateDevice.mutate(prepareValue(values), {
        onSuccess: (data) => {
          setResponse({
            type: intl.formatMessage(CalibrationIntl.success),
            message: intl.formatMessage(CalibrationIntl.successMessage),
            action: {
              title: intl.formatMessage(CalibrationIntl.done),
              method: () => closeAppDialog(),
            },
          });
          successfullyCalibrated();
        },
        onError: (error) => {
          setResponse({
            type: intl.formatMessage(CalibrationIntl.error),
            message: intl.formatMessage(CalibrationIntl.errorMessage),
            action: {
              title: intl.formatMessage(CalibrationIntl.tryAgain),
              method: () => closeAppDialog(),
            },
          });
          calibrationError();
        },
      });
    }
  };

  return (
    <Formik
      initialValues={{
        cameraHeight: undefined,
        cameraOffset: undefined,
        vehicleWidth: undefined,
        lowestUsableRow: 0,
        calpickTopLeftCol: 0,
        calpickTopLeftRow: 0,
        calpickBottomLeftCol: 0,
        calpickBottomLeftRow: 0,
        calpickTopRightCol: 0,
        calpickTopRightRow: 0,
        calpickBottomRightCol: 0,
        calpickBottomRightRow: 0,
      }}
      validate={validate[currentStep]}
      validateOnBlur
      onSubmit={async (values, bag) => {
        if (currentStep < steps.length) {
          bag.setSubmitting(false);
        }
        values && handleSubmit(values);
      }}
    >
      <>
        <div className={bem.elem('steps-title').cls()}>
          <p>{intl.formatMessage(CalibrationIntl.title)}</p>

          <MeasurementList show={currentStep === 1 || currentStep === 2} />
        </div>
        <Form>
          <section
            className={bem
              .elem('steps-box')
              .mods(currentStep === 1 && 'extandable')
              .cls()}
          >
            <div className={bem.elem('step-item').cls()}>
              {steps.map((x) => (
                <div
                  key={'step-' + x.id}
                  className={bem
                    .elem('step')
                    .mods(currentStep === x.id && 'current')
                    .cls()}
                >
                  {x.component}
                </div>
              ))}
            </div>
            <div className={bem.elem('step-navigation').cls()}>
              <div>
                <Button
                  color="primary"
                  className={bem
                    .elem('back-arrow')
                    .mods(currentStep === 1 && 'visible')
                    .cls()}
                  onClick={() => setCurrentStep((currentStep) => --currentStep)}
                >
                  {intl.formatMessage(CalibrationIntl.back)}
                </Button>
              </div>
              <div>
                <Button
                  disabled={!selectedImage.url?.url}
                  color="primary"
                  className={bem
                    .mods(currentStep === steps.length && 'final-step')
                    .cls()}
                  type={'submit'}
                >
                  {intl.formatMessage(
                    currentStep < steps.length ? CalibrationIntl.continue : CalibrationIntl.calibrate,
                  )}
                </Button>
              </div>
            </div>
          </section>
        </Form>
      </>
    </Formik>
  );
};
